import { memo } from 'react';
import classNames from 'classnames';
import styles from './ProductGrid.module.scss';
import { overwriteImage } from './ImageOverwrite';
import {
  ADD_TO_BASKET,
  ALTERNATE_IMAGE,
  dispatchCitrusAnalytics,
  dispatchImmediateAnalyticsEvent,
  GLOBAL_INTERACTION,
} from '../analytics/analyticsEvents';
import ProductCard from '@JohnLewisPartnership/jl-design-system/components/product-card/ProductCard';
import ProductCardDetailed from '@JohnLewisPartnership/jl-design-system/components/product-card/ALPHA/ProductCardDetailed';
import CompareCheckbox from '../compare-checkbox/CompareCheckbox';
import AdditionalMessage from '../additional-message/AdditionalMessage';
import { mapProduct } from '../analytics/utils/mapProduct';
import { useProductCardContainerData } from './data/useProductCardContainerData';
import { trackSingleSkuAddToBasket } from '../analytics/jlAnalytics';

export const getPromoModalProps = (product, handleDisplayPromoModalFn) => {
  return {
    hasPromoMessageModal: !product.isOutlet,
    handleDisplayPromoModal: handleDisplayPromoModalFn,
    isPromoQuickViewFlagOn: true,
  };
};

const ProductCardContainer = ({
  className = '',
  handleQuickViewClick = () => {},
  handleAddToBasketClick = () => {},
  handleDisplayM2MProductSelector = () => {},
  handleDisplayQuickAdd = () => {},
  hasCTAButton = true,
  hasQuickViewIcon = true,
  imageSize = '',
  imageWidths,
  index,
  isLargeCardSize = false,
  isLocalStorageAvailable = true,
  isMobileBreakpoint = false,
  monetateId = null,
  openEnergyDrawer = () => {},
  pricePrefix = '',
  product,
  socialProofingMessages = [],
  socialProofMessageExperiments = [],
}) => {
  const {
    basketBFFService,
    basketData,
    breadcrumbContext,
    compare,
    experiencesContext,
    isListView,
    path,
  } = useProductCardContainerData();

  const { category, breadcrumbs } = breadcrumbContext;

  const {
    atbBlockList = [],
    imageOverwriteFeature,
    isHeroAttributesFlagOn,
    isHiddenProductRatingFlagOn,
    isLimitedSwatchesFlagOn,
    isProductCardImageCarouselAltStartFlagOn,
    isProductCardImageCarouselDesktopFlagOn,
    isProductCardImageCarouselMobileFlagOn,
    isProductCardImageCarouselPipIndicatorsFlagOn,
    isStandaloneCompareFlagOn,
    isHideA2BbuttonFlagOn,
    isNewAtbAnalyticsFlagOn,
    isHideSwatchesFlagOn,
    isShowNumberOfSwatchesFlagOn,
    isCuratedCategoriesFlagOn,
  } = experiencesContext;

  const {
    adId,
    colorSwatches,
    credit,
    defaultSkuId,
    defaultVariantId,
    highlightedAttributes,
    id,
    image,
    isSponsored,
    additionalMessages,
    price,
    productId,
    promoMessages,
    selectedSwatch,
    title,
    type,
    url,
  } = product;

  const featuresArray = [];

  highlightedAttributes?.forEach((feature) => {
    featuresArray.push(
      `${feature.hideNameOnHighlight ? '' : feature.name?.concat(':') || ' '} ${
        feature.value || '  '
      }`
    );
  });

  const productEventData = mapProduct(product, index);
  delete productEventData.mb;

  const handleCompareClick = () => {
    compare.toggle(id, category);
    if (isSponsored) dispatchCitrusAnalytics(id, adId);
  };

  const hasCompare =
    product.compare && product.type === 'product' && isLocalStorageAvailable;
  const comparing = compare.products.includes(id || productId);
  const compareProps = hasCompare
    ? {
        checked: comparing,
        disabled: compare.isDisabled && !comparing,
        onChange: handleCompareClick,
      }
    : {};

  const swatchToUse = selectedSwatch?.isColorOfDefaultVariant
    ? undefined
    : selectedSwatch;

  const handleM2MClick = (e) => {
    e.preventDefault();
    const clickedElement = e.currentTarget;
    const targetHref = clickedElement
      ? clickedElement.getAttribute('href')
      : '';
    handleDisplayM2MProductSelector(targetHref);
  };

  const getClickHandlerByType = (event) => {
    switch (type) {
      case 'm2mFabric':
        return handleM2MClick(event);
      default:
        return null;
    }
  };

  const handleClick = (event) => getClickHandlerByType(event);

  const singleSkuAtbAnalytics = () => {
    dispatchImmediateAnalyticsEvent(ADD_TO_BASKET, {
      products: productEventData,
    });
    if (isNewAtbAnalyticsFlagOn) {
      trackSingleSkuAddToBasket(product, breadcrumbs, basketData);
    }

    if (adId) {
      dispatchCitrusAnalytics(id, adId);
    }
  };

  const handleDisplayPromoModal = () => {
    dispatchImmediateAnalyticsEvent(GLOBAL_INTERACTION, {
      type: 'plp',
      subType: 'promotions modal',
      additionalInfo: {},
      properties: {
        promotions: promoMessages,
      },
      products: [
        {
          brand: product.brand,
          sku: defaultSkuId,
          price: price,
          productName: title,
          productId: id,
        },
      ],
    });
  };

  const canonicalUrlToUse =
    colorSwatches &&
    colorSwatches.length > 0 &&
    colorSwatches[0].isColorOfDefaultVariant !== true
      ? colorSwatches[0].canonicalURL
      : url;

  const urlToUse = adId
    ? `${canonicalUrlToUse}#citrusAdId=${adId}`
    : canonicalUrlToUse;

  // eslint-disable-next-line no-unused-vars
  const handleAltImageChangeAnalytics = (prodId) => (defaultSkuId) => {
    dispatchImmediateAnalyticsEvent(ALTERNATE_IMAGE, {
      productId: prodId,
    });
  };

  const getComponentPromoModalProps = (product) => {
    return getPromoModalProps(product, handleDisplayPromoModal);
  };

  const getCreditMessage = () => {
    if (!credit) return null;

    const { financialProductType, repaymentAmount, creditRate } =
      credit.illustration || {};

    if (!price.now.from) {
      if (financialProductType === 'IBC') {
        return `From ${repaymentAmount.display} a month ${creditRate.display} APR*`;
      }
      if (financialProductType === 'IFC') {
        return `From ${repaymentAmount.display} a month interest free*`;
      }
    } else if (credit.available) {
      return 'Payment plans available';
    }

    return null;
  };

  const shouldShowAltImageOnHover =
    !isMobileBreakpoint && !product.isMadeToMeasure;

  const colorSwatchesToShow =
    (!isHideSwatchesFlagOn &&
      product.type !== 'productSet' &&
      product.colorSwatches) ||
    null;

  const isProductSet = type === 'productSet';

  if (isCuratedCategoriesFlagOn) {
    return (
      <div
        data-testid="component-grid-column"
        className={classNames(
          className,
          styles['product-grid__product'],
          styles['product-grid__product--curated-categories']
        )}
      >
        <ProductCard
          product={{
            ...product,
            colorSwatches: [],
            promoMessages: [],
            price: {},
            energy: undefined,
            image: overwriteImage({
              imageOverwriteFeature,
              path,
              defaultSkuId: product.defaultSkuId,
              image: product.image,
            }),
            skuId: defaultSkuId,
            url: urlToUse,
            variantId: defaultVariantId,
          }}
          className={classNames(styles.product)}
          buttonSettings={{
            hasAddToBasketButton: false,
            hasEmailMeWhenAvailableButton: false,
            hasMoreInformationButton: false,
            hasNotifyMeButton: false,
            hasQuickViewButton: false,
          }}
          handleClick={handleClick}
          handleImageChange={handleAltImageChangeAnalytics(id)}
          hasAltImageOnHover={shouldShowAltImageOnHover}
          hasBrandedTitle
          hasEnergySideDrawer={false}
          imageSettings={{
            loading: index < 4 ? 'eager' : 'lazy',
            sizes: imageSize,
            srcSetWidths: imageWidths,
            aspectRatio: 0.75,
          }}
          hasWishlistPlaceholder
          hasProductRating={false}
        ></ProductCard>
      </div>
    );
  }

  return (
    <div
      data-testid="component-grid-column"
      className={classNames(className, styles['product-grid__product'], {
        [styles['product-grid__product--list-view']]: isListView,
        [styles['product-grid__product--large']]: isLargeCardSize,
      })}
      data-grid-checked={isLargeCardSize ? 'max' : 'min'}
    >
      {isListView ? (
        <ProductCardDetailed
          product={{
            ...product,
            additionalMessages,
            colorSwatches: colorSwatchesToShow,
            image: overwriteImage({
              imageOverwriteFeature,
              path,
              defaultSkuId: product.defaultSkuId,
              image,
            }),
            creditMessage: getCreditMessage(),
            promoMessages,
            url: isProductSet && url ? `/browse${url}` : urlToUse,
            skuId: defaultSkuId,
            variantId: defaultVariantId,
          }}
          handleQuickAddClick={handleDisplayQuickAdd}
          handleQuickViewClick={handleQuickViewClick}
          buttonSettings={{
            hasAddToBasketButton: !atbBlockList.includes(product.defaultSkuId),
            hasEmailMeWhenAvailableButton: true,
            hasMoreInformationButton: true,
            hasNotifyMeButton: true,
          }}
          hasCompare={hasCompare}
          compareProps={compareProps}
          handleEnergyRatingClick={openEnergyDrawer}
          hasDiorPromosEnabled
          hasSwatchTextButNoRoundels={isShowNumberOfSwatchesFlagOn}
          hasEnergySideDrawer
          hasHeroAttributes={isHeroAttributesFlagOn}
          hasLimitedSwatches={isLimitedSwatchesFlagOn}
          imageSettings={{
            loading: index < 4 ? 'eager' : 'lazy',
          }}
          monetateId={monetateId}
          pricePrefix={pricePrefix}
          selectedSwatch={swatchToUse}
          socialProofMessage={
            socialProofingMessages[id]?.title
              ? socialProofingMessages[id]?.title
              : null
          }
          socialProofMessageExperiments={
            socialProofMessageExperiments ? socialProofMessageExperiments : []
          }
          sponsoredTitleEnabled={isSponsored}
          atbProps={{
            clientId: 'jl-standard-plp',
            path: basketBFFService,
            onBeforeAdd: () => handleAddToBasketClick(productEventData),
            onSuccess: productEventData.isMultiSku
              ? undefined
              : singleSkuAtbAnalytics,
          }}
          {...getComponentPromoModalProps(product)}
          hasWishlistPlaceholder
          handleSwatchClick={() =>
            isSponsored && dispatchCitrusAnalytics(id, adId)
          }
        />
      ) : (
        <ProductCard
          product={{
            ...product,
            colorSwatches: colorSwatchesToShow,
            image: overwriteImage({
              imageOverwriteFeature,
              path,
              defaultSkuId: product.defaultSkuId,
              image: product.image,
            }),
            creditMessage: getCreditMessage(),
            price,
            promoMessages,
            skuId: defaultSkuId,
            url: isProductSet && url ? `/browse${url}` : urlToUse,
            variantId: defaultVariantId,
            energy: product.energy,
          }}
          hasShortCtaText
          className={classNames(styles.product)}
          handleQuickAddClick={handleDisplayQuickAdd}
          buttonSettings={{
            hasAddToBasketButton: isHideA2BbuttonFlagOn
              ? false
              : !atbBlockList.includes(product.defaultSkuId) && hasCTAButton,
            hasEmailMeWhenAvailableButton: hasCTAButton,
            hasMoreInformationButton:
              isHideA2BbuttonFlagOn && !isProductSet ? false : hasCTAButton,
            hasNotifyMeButton: hasCTAButton,
            hasQuickViewButton: false,
          }}
          handleEnergyRatingClick={openEnergyDrawer}
          hasDiorPromosEnabled
          hasSwatchTextButNoRoundels={isShowNumberOfSwatchesFlagOn}
          hasEnergySideDrawer
          handleQuickViewClick={handleQuickViewClick}
          handleClick={handleClick}
          handleImageChange={handleAltImageChangeAnalytics(id)}
          hasBrandedTitle
          imageSettings={{
            loading: index < 4 ? 'eager' : 'lazy',
            sizes: imageSize,
            srcSetWidths: imageWidths,
            aspectRatio: 0.75,
          }}
          hasImageCarousel={isProductCardImageCarouselDesktopFlagOn}
          hasMobileImageCarousel={isProductCardImageCarouselMobileFlagOn}
          hasCarouselAltStart={isProductCardImageCarouselAltStartFlagOn}
          hasCarouselPipIndicators={
            isProductCardImageCarouselPipIndicatorsFlagOn &&
            isProductCardImageCarouselMobileFlagOn
          }
          hasAltImageOnHover={shouldShowAltImageOnHover}
          isFirstProductCard={index === 0}
          isProductBadgingOn={true}
          isQuickViewIcon={hasQuickViewIcon}
          monetateId={monetateId}
          pricePrefix={pricePrefix}
          selectedSwatch={swatchToUse}
          sponsoredTitleEnabled={isSponsored}
          socialProofMessage={
            socialProofingMessages[id]?.title
              ? socialProofingMessages[id]?.title
              : null
          }
          socialProofMessageExperiments={socialProofMessageExperiments}
          atbProps={{
            clientId: 'jl-standard-plp',
            onSuccess: productEventData.isMultiSku
              ? undefined
              : singleSkuAtbAnalytics,
            path: basketBFFService,
            onBeforeAdd: () => handleAddToBasketClick(productEventData),
          }}
          {...getComponentPromoModalProps(product)}
          handleSwatchClick={() =>
            isSponsored && dispatchCitrusAnalytics(id, adId)
          }
          hasWishlistPlaceholder
          hasProductRating={!isHiddenProductRatingFlagOn}
        >
          {hasCompare &&
            (isStandaloneCompareFlagOn ? (
              <jl-compare-checkbox
                pim-product-id={product.id}
                ad-id={isSponsored ? adId : ''}
              ></jl-compare-checkbox>
            ) : (
              <CompareCheckbox
                ariaLabel={`Select for product comparison, ${title}`}
                checked={comparing}
                disabled={compare.isDisabled && !comparing}
                id={`checkbox-${product.id}`}
                onCompareClick={handleCompareClick}
              />
            ))}
          {additionalMessages &&
            additionalMessages.map((additionalMessage) => (
              <AdditionalMessage
                key={additionalMessage}
                message={additionalMessage}
              />
            ))}
        </ProductCard>
      )}
    </div>
  );
};

const comparisonFn = (prevProps, nextProps) => {
  return (
    prevProps.product.id === nextProps.product.id &&
    prevProps.product.image === nextProps.product.image &&
    prevProps.socialProofingMessages === nextProps.socialProofingMessages &&
    prevProps.isLargeCardSize === nextProps.isLargeCardSize &&
    prevProps.isLocalStorageAvailable === nextProps.isLocalStorageAvailable
  );
};

export default memo(ProductCardContainer, comparisonFn);
